import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, ChevronRight, Loader2, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import OrderCaseDialog from './OrderCaseDialog';
import {
  CASE_ACTION_LABELS, CASE_STATUS_META, CASE_TYPE_META, caseApi, formatDate,
} from '../lib/orderCases';

/**
 * Blocco contestuale "Hai bisogno di aiuto con questo ordine?" da mostrare nel dettaglio ordine.
 * Le azioni disponibili dipendono dal tipo di fornitura dell'ordine e dalla finestra di reso.
 */
export default function OrderHelpBlock({ order }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState(null);

  const load = async () => {
    if (eligibility || loading) return;
    setLoading(true);
    try {
      setEligibility(await caseApi.eligibility(order.id));
    } catch (e) {
      setEligibility({ allowed_case_types: [], open_cases: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const openCases = eligibility?.open_cases || [];

  return (
    <div
      className="mt-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden"
      data-testid={`order-help-block-${order.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors"
        data-testid={`order-help-toggle-${order.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#008080]/10 flex items-center justify-center">
            <LifeBuoy className="w-4 h-4 text-[#008080]" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-800">Hai bisogno di aiuto con questo ordine?</p>
            <p className="text-xs text-slate-500">Resi, difetti, consegna: risolviamo insieme</p>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-slate-100">
              {loading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Controllo le opzioni disponibili…
                </div>
              ) : (
                <>
                  {openCases.length > 0 && (
                    <div className="space-y-2 mb-4 mt-3">
                      {openCases.map((c) => {
                        const meta = CASE_TYPE_META[c.type] || CASE_TYPE_META.OTHER;
                        const status = CASE_STATUS_META[c.status] || {};
                        return (
                          <button
                            key={c.id}
                            onClick={() => navigate(`/assistenza-ordine/${c.id}`)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-[#008080]/40 hover:shadow-sm transition-all text-left"
                            data-testid={`existing-case-${c.id}`}
                          >
                            <meta.Icon className={`w-4 h-4 ${meta.accent}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{meta.label}</p>
                              <p className="text-xs text-slate-400">{c.case_number} · {formatDate(c.created_at)}</p>
                            </div>
                            <Badge className={`${status.cls} border text-xs`}>{status.label || c.status}</Badge>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {eligibility?.return_blocked_reason && (
                    <div className="flex gap-2 items-start rounded-xl bg-amber-50 border border-amber-100 p-3 mb-4 mt-3">
                      <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800 leading-relaxed">{eligibility.return_blocked_reason}</p>
                    </div>
                  )}

                  {eligibility?.return_available && (
                    <div className="flex gap-2 items-start rounded-xl bg-emerald-50 border border-emerald-100 p-3 mb-4 mt-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-emerald-800 leading-relaxed">
                        Reso disponibile: hai ancora {eligibility.days_left} giorni
                        {eligibility.return_deadline ? ` (fino al ${formatDate(eligibility.return_deadline)})` : ''}.
                      </p>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-2">
                    {(eligibility?.allowed_case_types || []).map((type, idx) => {
                      const meta = CASE_TYPE_META[type];
                      return (
                        <motion.button
                          key={type}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.04 }}
                          onClick={() => setActiveType(type)}
                          data-testid={`help-action-${type}`}
                          className="group flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 hover:border-[#008080]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left"
                        >
                          <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                            <meta.Icon className={`w-4 h-4 ${meta.accent}`} />
                          </div>
                          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                            {CASE_ACTION_LABELS[type]}
                          </span>
                        </motion.button>
                      );
                    })}
                  </div>

                  {!eligibility?.allowed_case_types?.length && (
                    <p className="text-sm text-slate-500 py-3">
                      Nessuna azione disponibile per questo ordine. Puoi comunque scrivere all'artigiano dai messaggi.
                    </p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {activeType && (
        <OrderCaseDialog
          open={!!activeType}
          onOpenChange={(v) => !v && setActiveType(null)}
          order={order}
          caseType={activeType}
          eligibility={eligibility}
          userId={user?.id}
        />
      )}
    </div>
  );
}
